import axios from 'axios';
import getAuthSession from './auth';
import { QCAdminResponse, QCReview, QCRun } from '../interfaces/qcReview.interface';
import { QCReviewItem } from '../interfaces/qcReviewData.interface';

// POST /qc/workflows/<workflow_id>/run/ (qc/views.py: RunQCAPIView). Fire-and-forget by
// design (handoff §3) — the backend enqueues the run and emails the triggering admin when
// it's ready; the UI never polls QCRun.status.
export async function triggerAssessmentQC(workflowId: number): Promise<QCRun> {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/run/`,
    {},
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCRun;
}

// GET /qc/workflows/<workflow_id>/reviews/ (qc/views.py: QCReviewListAPIView). Returns the
// current QCReview rows for the workflow's assessment survey as a plain array (no pagination).
export async function getQCReviews(workflowId: number): Promise<QCReview[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/reviews/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCReview[];
}

// GET /qc/workflows/<workflow_id>/review-data/ (qc/views.py: AssessmentQCReviewDataAPIView).
// One fully-resolved bundle per NI/PI AssessorAnswer — question + sub-controls, SP
// disposition, evidence, comment thread — replacing the fetchAssessment/fetchSurvey/
// fetchDetailedQuestionnaire + per-item evidence/comment fetch chain the review page used.
export async function getQCReviewData(workflowId: number): Promise<QCReviewItem[]> {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/qc/workflows/${workflowId}/review-data/`,
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCReviewItem[];
}

// PATCH /qc/reviews/<qc_review_id>/admin-response/ (qc/views.py: QCReviewAdminResponseAPIView).
// Records the admin's accept/edit/reject reaction to ONE QC verdict - the ADR-0005
// agreement-rate signal. Orthogonal to AssessorAnswer.verification_status (see
// updateAssessorAnswer in services/assessments.ts) - never set both from one control
// (ADR-0007). admin_response_by/admin_response_at are stamped server-side, not sent here.
export async function updateQCReviewAdminResponse(
  qcReviewId: number,
  adminResponse: QCAdminResponse,
): Promise<QCReview> {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/qc/reviews/${qcReviewId}/admin-response/`,
    { adminResponse },
    {
      headers: { Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as QCReview;
}
